import {
  camelCaseKeysDeep,
  processNotificationSocketMessage,
  type NotificationSocketHandlers,
  type WebSocketStatus,
} from '@repo/utils';

type NotificationWebSocketConfig = {
  url: string;
  token: string;
  handlers: NotificationSocketHandlers;
  onStatusChange?: (status: WebSocketStatus) => void;
  onError?: (error: Event) => void;
};

export class NotificationWebSocketService {
  private ws: WebSocket | null = null;
  private config: NotificationWebSocketConfig | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isManualClose = false;

  connect(config: NotificationWebSocketConfig) {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }

    if (this.ws && this.ws.readyState !== WebSocket.CONNECTING) {
      this.ws.close();
      this.ws = null;
    }

    this.config = config;
    this.isManualClose = false;
    this.initializeWebSocket();
  }

  private initializeWebSocket() {
    if (!this.config) {
      return;
    }

    const { url, token, handlers, onStatusChange, onError } = this.config;

    try {
      onStatusChange?.('connecting');

      const socketUrl = new URL(url);
      socketUrl.searchParams.set('one_time_token', token);
      const urlWithToken = socketUrl.toString();

      this.ws = new WebSocket(urlWithToken);

      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        onStatusChange?.('connected');
      };

      this.ws.onmessage = event => {
        try {
          if (!event.data) return;

          const rawData = JSON.parse(event.data);
          const data = camelCaseKeysDeep<Record<string, unknown>>(rawData);
          processNotificationSocketMessage(data, handlers);
        } catch (error) {
          console.error('[notifications-ws] parse error', error);
        }
      };

      this.ws.onerror = error => {
        onStatusChange?.('error');
        onError?.(error);
      };

      this.ws.onclose = () => {
        onStatusChange?.('disconnected');

        if (!this.isManualClose && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      onStatusChange?.('error');
      console.error('[notifications-ws] init error', error);
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;
    this.reconnectTimer = setTimeout(() => this.initializeWebSocket(), delay);
  }

  disconnect() {
    this.isManualClose = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.reconnectAttempts = 0;
    this.config = null;
  }
}

let notificationWebSocketInstance: NotificationWebSocketService | null = null;

export const getNotificationWebSocket = (): NotificationWebSocketService => {
  if (!notificationWebSocketInstance) {
    notificationWebSocketInstance = new NotificationWebSocketService();
  }
  return notificationWebSocketInstance;
};
